import type { W_Job_Create_RM, W_Job_Create_VM } from 'ab-api/web/job/create';
import type { W_Job_FindJobForm_VM } from 'ab-api/web/job/findJobForm';

import type JobUpsertVM from 'ab-viewModels/workRequest/jobUpsert.viewModel';
import type { WorkLocationAddressVM } from 'ab-viewModels/workRequest/workRequestDetailsUpsert.viewModel';

import type * as JobUpsertRM from 'ab-requestModels/workRequest/jobUpsert.requestModel';

import { getId } from '@acceligentllc/shared/utils/array';
import { EMAIL_REGEX } from '@acceligentllc/shared/utils/email';
import { isNullOrUndefined } from '@acceligentllc/shared/utils/extensions';
import { toRawPhoneNumber } from '@acceligentllc/shared/utils/phone';
import { isValidTextInput } from '@acceligentllc/shared/utils/text';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { AccountPermissionTemplate } from '@acceligentllc/shared/enums/accountPermissionTemplate';
import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import type { EmailTypes, PhoneTypes } from '@acceligentllc/shared/enums/contactMethodType';
import type { CountryCode } from '@acceligentllc/shared/enums/countryCode';
import type { DeliverableDataType } from '@acceligentllc/shared/enums/deliverableDataType';
import type { DirectoryIcon } from '@acceligentllc/shared/enums/directoryIcon';
import type { FileType } from '@acceligentllc/shared/enums/fileType';
import type { Priority } from '@acceligentllc/shared/enums/priority';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import type { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import type { TimeSheetEntryWorkType } from '@acceligentllc/shared/enums/timeSheetEntryWorkType';
import type { WorkRequestDisposalMethod } from '@acceligentllc/shared/enums/workRequestDisposalMethod';
import type { WorkRequestJobHazardAssessmentStatus } from '@acceligentllc/shared/enums/workRequestJobHazardAssessmentStatus';
import type { WorkRequestStatus } from '@acceligentllc/shared/enums/workRequestStatus';

import type { BillingCodeEditMode } from 'ab-enums/billingCodeEditMode.enum';

import { MAX_BILLING_CODE_UNIT_PRICE_VALUE, MAX_WR_TRUCK_EQUIPMENT_ADDITIONAL_FIELDS } from 'ab-common/constants/value';

export class InvoiceReminderRecipientFM {
	id: number;
	workRequestId: number;
	accountId: number;

	firstName: string;
	lastName: string;
	uniqueId: string;
	email: Nullable<string>;
	accountDeletedAtFlag: boolean;
}

class PayrollDifferentialFM {
	value: string;
	workTypes: TimeSheetEntryWorkType[];
}

class AccountFM {
	id: number;
	firstName: string;
	lastName: string;
}

class DeliverableDataFM {
	id: number;
	name: string;
	type: DeliverableDataType;
}

class EmployeeFM {
	id: number;
	firstName: string;
	lastName: string;
	fullName: string;
}

class ContactMethodFM {
	id: number;
	contactId: number;
	type: EmailTypes | PhoneTypes;
	value: string;
	countryCode: Nullable<CountryCode>;
	extension: Nullable<string>;
}

class ContactAddressFM {
	id: number;
	contactId: number;
	addressId: number;
	address: AddressFM;
}

class ContactFM {
	id: number;
	workOrderContactId: number;
	role: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;

	addresses: ContactAddressFM[];
	emails: ContactMethodFM[];
	phones: ContactMethodFM[];

	contactAddressIds: number[];
	contactEmailIds: number[];
	contactPhoneIds: number[];
}

class OfficeFM {
	id: number;
	nickname: string;
}

class DivisionFM {
	id: number;
	name: string;
}

class BillingCodeFM {
	id: number | string;
	databaseId: Nullable<number>;
	lineItemNumber: Nullable<number>;
	customerNumber: Nullable<number>;
	customerId: string;
	ownerNumber: Nullable<string>;
	ownerId: Nullable<string>;
	unit: Nullable<QuantityUnitType>;
	unitPrice: string;
	bidQuantity: Nullable<string>;
	groupId: Nullable<number>; // DB id ONLY
	groupKey: Nullable<string>; // UI id groups not yet in DB, else id - we need this since in same form we can create and assign groups
	group: Nullable<string>;
	groupColor: Nullable<ColorPalette>;
	description: string;
	deletedAt: Nullable<Date>;
	editMode: Nullable<BillingCodeEditMode>;
	createdAt: Date;
}

export class BillingCodeGroupFM {
	id: number | string;
	sortIndex: number;
	databaseId: Nullable<number>;
	name: string;
	color: Nullable<ColorPalette>;
	deletedAt: Nullable<Date>;
}

class AddressFM {
	id: number;
	street: string;
	streetNumber: Nullable<string>;
	route: Nullable<string>;
	suite: Nullable<string>;
	city: Nullable<string>;
	state: Nullable<string>;
	aa2: Nullable<string>;
	aa3: Nullable<string>;
	zip: Nullable<string>;
	postalOfficeBoxCode: Nullable<string>;
	country: Nullable<string>;
	latitude: number;
	longitude: number;
}

interface WorkLocationAddressBackupFM {
	aa1: Nullable<string>;
	aa2: Nullable<string>;
	aa3: Nullable<string>;
	country: Nullable<string>;
	latitude: Nullable<number>;
	longitude: Nullable<number>;
	locality: Nullable<string>;
	postalCode: Nullable<string>;
	street: Nullable<string>;
	streetNumber: Nullable<string>;
	suite: Nullable<string>;
	postalOfficeBoxCode: Nullable<string>;
	neighborhood: Nullable<string>;
	formattedAddress: Nullable<string>;
}

export class WorkLocationAddressFM {
	id: number;
	streetNumber: Nullable<string>;
	street: Nullable<string>;
	state: Nullable<string>;
	city: Nullable<string>;
	neighborhood: Nullable<string>;
	country: Nullable<string>;
	postalCode: Nullable<string>;
	notes: Nullable<string>;
	location: Nullable<string>;
	backup: Nullable<WorkLocationAddressBackupFM>;

	public static fromVM = (vm: WorkLocationAddressVM): WorkLocationAddressFM => {
		return {
			id: vm.id,
			streetNumber: vm.streetNumber,
			street: vm.street,
			state: vm.state,
			city: vm.city,
			neighborhood: vm.neighborhood,
			country: vm.country,
			postalCode: vm.postalCode,
			notes: vm.notes,
			location: WorkLocationAddressFM._formatLocation(vm),
			backup: {
				latitude: vm.latitude,
				longitude: vm.longitude,
				aa1: null,
				aa2: null,
				aa3: null,
				country: null,
				locality: null,
				postalCode: null,
				street: null,
				streetNumber: null,
				suite: null,
				postalOfficeBoxCode: null,
				neighborhood: null,
				formattedAddress: null,
			},
		};
	};

	private static _formatLocation(address: WorkLocationAddressVM) {
		const location: string[] = [];
		if (address.street) {
			location.push(address.street);
		}
		if (address.neighborhood) {
			location.push(address.neighborhood);
		}
		if (address.city) {
			location.push(address.city);
		}
		return `${address.streetNumber ? address.streetNumber : ''} ${location.join(', ')}`;
	}
}

interface UserInfoFM {
	accountId?: Nullable<number>;
	userId?: number;
	firstName?: string;
	lastName?: string;
	fullName: string;
}

class WorkRequestDirectoryFM {
	id: number;
	name: string;
	parentId: Nullable<number>;
	icon: Nullable<DirectoryIcon>;
	lastModifiedAt: Nullable<Date>;
	lastModifiedBy: Nullable<UserInfoFM>;
	attachmentIds: number[];
}

interface WorkOrderForAttachmentFM {
	id: number;
	code: string;
}

interface AttachmentFM {
	id: number;
	size: number;
	name: string;
	type: FileType;
	status: ResourceStatus;
	storageName: string;
	storageContainer: string;
	/** original version with presigned url */
	originalSrc: string;
	copyToWorkOrderFlag: boolean;
	lastModifiedAt: Date;
	uploadedBy: Nullable<UserInfoFM>;
	workOrders?: WorkOrderForAttachmentFM[];
}

interface SalesFM {
	id: number;
	fullName: string;
}

interface WorkRequestStatusFM {
	id: number;
	name: string;
	description: Nullable<string>;
	color: ColorPalette;
}

interface EmployeeOptionFM {
	id: number;
	formattedCode: string;
	firstName: string;
	lastName: string;
	uniqueId: string;
	accountTemplate: AccountPermissionTemplate;
	fullName: string;
}

export class SubjobFM {
	id: number;
	jobCode: string;
	isInternal: boolean;
	title: Nullable<string>;
	customerCompany: Nullable<string>;
	customerFormatted: Nullable<string>;
	travelLocationShort: Nullable<string>;

	static fromJobUpsertFM = (job: JobUpsertFM): SubjobFM => {
		const customerCompany = job.customerCompanyName ?? job.customerContact?.companyName ?? null;
		return {
			id: job.id,
			jobCode: job.jobCode,
			isInternal: job.isInternal,
			customerCompany,
			customerFormatted: job.customerContact?.fullName ? `${job.customerContact.fullName}${customerCompany ? `, ${customerCompany}` : ''}` : null,
			title: job.title,
			travelLocationShort: job.travelLocationShort,
		};
	};
}

export type AdditionalTrucksEquipmentField = {
	name: string;
	isChecked: boolean;
};

class JobUpsertFM {
	id: number; // Necessary when creating job through WO page
	jobCode: string;
	title: string;
	salesId: Nullable<string>;
	salesLead: Nullable<SalesFM>;
	salesLeadId: Nullable<number>;
	customerCompanyName: Nullable<string>;
	workLocation: Nullable<WorkLocationAddressFM>;
	projectManager: Nullable<EmployeeFM>;
	projectManagerId: Nullable<number>;
	officeId: Nullable<number>;
	office: Nullable<OfficeFM>;
	divisionId: Nullable<number>;
	division: Nullable<DivisionFM>;
	isInternal: boolean;
	jobNotes: Nullable<string>;
	estimateTotal: number;
	status: WorkRequestStatus;
	priority: Priority;

	customerReferencePO: Nullable<string>;
	isEmergencyJob: boolean;
	jobStatusId: Nullable<number>;
	jobStatus: Nullable<WorkRequestStatusFM>;
	requestBondRequirementsNote: Nullable<string>;
	requestHSERequirementsNote: Nullable<string>;
	requestInsuranceRequirementsNote: Nullable<string>;
	requestJobNote: Nullable<string>;
	requestLiquidatedDamagesNote: Nullable<string>;
	requestMBEOrWBERequirementsNote: Nullable<string>;
	retainage: Nullable<string>;
	targetDaysFromStart: Nullable<number>;
	targetStartDate: Nullable<string>;

	projectOwner: Nullable<string>;
	ownerReference: Nullable<string>;
	taxExempt: boolean;
	generalContractor: Nullable<string>;
	contractorReference: Nullable<string>;
	isUnion: boolean;
	isPrevailingWage: boolean;
	jurisdiction: Nullable<string>;
	payrollDifferential: Nullable<PayrollDifferentialFM>;

	surveyNeeded: boolean;
	surveyedById: Nullable<number>;
	surveyedBy: Nullable<EmployeeOptionFM>;
	surveyDate: Nullable<string>;
	waterAvailable: boolean;
	waterSupplyInformation: Nullable<string>;
	subcontractorNeeded: Nullable<string>;
	subcontractorInformation: Nullable<string>;
	pipeSizes: Nullable<string>;
	pipeTypes: Nullable<string>;
	manholeDepths: Nullable<string>;
	manholeFrameDiameters: Nullable<string>;
	informationsProvidedByCustomersBy: Nullable<string>;

	jc80gpm: boolean;
	sjc80gpm: boolean;
	jcRecycler80: boolean;
	jjc120gpm: boolean;
	jjc160gpm: boolean;
	jcHiRail: boolean;
	jetter: boolean;
	harbenJetter: boolean;
	hjJetter: boolean;
	tvTruck: boolean;
	tvLateralTk: boolean;
	tvGroutTk: boolean;
	tvLatGroutTk: boolean;
	tvSonarTk: boolean;
	tvPolaris: boolean;
	tvLCRTk: boolean;
	tvLateralReinstator: boolean;
	tvMTHTk: boolean;
	tvLaserTk: boolean;
	minicam: boolean;
	vacTk: boolean;
	hivacTk: boolean;
	hiRailvacTk: boolean;
	tanker: boolean;
	wb: boolean;
	hwBoiler: boolean;
	pressureWash: boolean;
	airComp: boolean;
	clamTk: boolean;
	bucketMach: boolean;
	mhRehabTk: boolean;
	microtrax: boolean;
	smallEasementMachine: boolean;
	bigEasementMachine: boolean;
	arrowBoard: boolean;
	supportTk: boolean;
	rollOffTk: boolean;
	pump: boolean;
	pigLaunch: boolean;
	stoveMach: boolean;
	powerRodder: boolean;
	reeferTruck: boolean;
	lateralReinstatorTrailer: boolean;
	steamCureTruck: boolean;
	airTesting: boolean;
	trackMachine: boolean;
	additionalTruckEquipmentFields: AdditionalTrucksEquipmentField[];
	numberOfEmployees: number;

	customerContact: Nullable<ContactFM>;
	siteContact: Nullable<ContactFM>;
	billingContact: Nullable<ContactFM>;
	billTo: Nullable<string>;
	billToAddress: Nullable<string>;
	billToCityStateZip: Nullable<string>;
	billToPhoneNumber: Nullable<string>;
	billToFaxNumber: Nullable<string>;
	billToContactName: Nullable<string>;
	billToEmail: Nullable<string>;
	additionalContacts: Nullable<ContactFM[]>;
	travelLocationShort: Nullable<string>;
	invoiceReminderRecipients: InvoiceReminderRecipientFM[];

	isDeliverable: boolean;
	deliverableAssigneeId: Nullable<number>;
	deliverableAssignee: Nullable<AccountFM>;
	deliverableSoftwareId: Nullable<number>;
	deliverableSoftware: Nullable<DeliverableDataFM>;
	deliverableFileFormatId: Nullable<number>;
	deliverableFileFormat: Nullable<DeliverableDataFM>;
	deliverableCodeStandardId: Nullable<number>;
	deliverableCodeStandard: Nullable<DeliverableDataFM>;
	deliverableDeliveryMethodId: Nullable<number>;
	deliverableDeliveryMethod: Nullable<DeliverableDataFM>;
	deliverableDeliveryTimelineId: Nullable<number>;
	deliverableDeliveryTimeline: Nullable<DeliverableDataFM>;
	deliverableNotes: Nullable<string>;

	allowCustomerSignature: boolean;

	startDate: Nullable<string>;
	guaranteedCompletionDate: Nullable<string>;
	targetCompletionDate: Nullable<string>;
	targetedDaysFromStart: Nullable<number>;
	guaranteedDaysFromStart: Nullable<number>;

	// Disposal
	ableToCleanAndWashEquipmentAtCustomerLocation: boolean;
	cleanAndWashLocation: Nullable<string>;
	isWasteGeneratedSolid: boolean;
	isWasteGeneratedSludge: boolean;
	isWasteGeneratedLiquid: boolean;
	isWasteHazardous: boolean;
	isWasteManifest: boolean;
	wasteDescription: Nullable<string>;
	expectedWasteQuantity: Nullable<number>;
	disposalMethod: Nullable<WorkRequestDisposalMethod>;
	disposalInstructions: Nullable<string>;
	disposalContact: Nullable<ContactFM>;

	billingCodes: BillingCodeFM[];
	billingCodeGroups: BillingCodeGroupFM[];

	// Job Hazard Summary
	hazardSummary: Nullable<string>;
	permitsRequired: Nullable<string[]>;

	// Safety
	environmentalExposure: boolean;
	environmentalExposureDescription: Nullable<string>;
	respiratoryProtection: boolean;
	respiratoryProtectionDescription: Nullable<string>;
	overheadHazards: boolean;
	overheadHazardsDescription: Nullable<string>;
	heightWork: boolean;
	heightWorkDescription: Nullable<string>;
	mechanicalHazards: boolean;
	mechanicalHazardsDescription: Nullable<string>;
	electricalHazards: boolean;
	electricalHazardsDescription: Nullable<string>;
	hazardousMaterials: boolean;
	hazardousMaterialsDescription: Nullable<string>;
	dangerousAnimalsOrPlants: boolean;
	dangerousAnimalsOrPlantsDescription: Nullable<string>;

	// Environmental Protection
	waterOrWetlands: boolean;
	waterOrWetlandsDescription: Nullable<string>;
	stormDrain: boolean;
	stormDrainDescription: Nullable<string>;
	bypassPumping: boolean;
	bypassPumpingDescription: Nullable<string>;
	sensitiveEnvironment: boolean;
	sensitiveEnvironmentDescription: Nullable<string>;

	// Public Safety
	vehicleTraffic: boolean;
	vehicleTrafficDescription: Nullable<string>;
	pedestrianCrossing: boolean;
	pedestrianCrossingDescription: Nullable<string>;

	// Status
	jobHazardAssessmentStatus: Nullable<WorkRequestJobHazardAssessmentStatus>;

	directories: WorkRequestDirectoryFM[];
	workRequestAttachments: AttachmentFM[];
	uploadedAttachmentIds?: number[];

	uploadingAttachments?: Record<string, true>;

	isProject: boolean;
	subjobs: SubjobFM[];
	projectId: Nullable<number>;

	constructor(vm: JobUpsertVM) {
		this.id = vm.id;
		this.jobCode = vm.jobCode;
		this.title = vm.title;
		this.salesId = vm.salesId;
		this.salesLead = vm.salesLead;
		this.salesLeadId = vm.salesLeadId;
		this.customerCompanyName = vm.customerCompanyName;
		this.workLocation = vm.workLocation ? WorkLocationAddressFM.fromVM(vm.workLocation) : null;
		this.projectManager = vm.projectManager;
		this.projectManagerId = vm.projectManagerId;
		this.officeId = vm.officeId;
		this.office = vm.office;
		this.divisionId = vm.divisionId;
		this.division = vm.division;
		this.isInternal = vm.isInternal;
		this.jobNotes = vm.jobNotes;
		this.estimateTotal = vm.estimateTotal;
		this.status = vm.status;
		this.priority = vm.priority;
		this.customerReferencePO = vm.customerReferencePO;
		this.isEmergencyJob = vm.isEmergencyJob;
		this.jobStatusId = vm.jobStatusId;
		this.jobStatus = vm.jobStatus;
		this.requestBondRequirementsNote = vm.requestBondRequirementsNote;
		this.requestHSERequirementsNote = vm.requestHSERequirementsNote;
		this.requestInsuranceRequirementsNote = vm.requestInsuranceRequirementsNote;
		this.requestJobNote = vm.requestJobNote;
		this.requestLiquidatedDamagesNote = vm.requestLiquidatedDamagesNote;
		this.requestMBEOrWBERequirementsNote = vm.requestMBEOrWBERequirementsNote;
		this.retainage = vm.retainage;
		this.targetDaysFromStart = vm.targetDaysFromStart;
		this.targetStartDate = vm.targetStartDate;
		this.travelLocationShort = vm.travelLocationShort;
		this.projectOwner = vm.projectOwner;
		this.ownerReference = vm.ownerReference;
		this.taxExempt = vm.taxExempt;
		this.generalContractor = vm.generalContractor;
		this.contractorReference = vm.contractorReference;
		this.isUnion = vm.isUnion;
		this.isPrevailingWage = vm.isPrevailingWage;
		this.jurisdiction = vm.jurisdiction;
		this.payrollDifferential = vm.payrollDifferential;
		this.surveyNeeded = vm.surveyNeeded;
		this.surveyedById = vm.surveyedById;
		this.surveyedBy = vm.surveyedBy;
		this.surveyDate = vm.surveyDate;
		this.waterAvailable = vm.waterAvailable;
		this.waterSupplyInformation = vm.waterSupplyInformation;
		this.subcontractorNeeded = vm.subcontractorNeeded;
		this.subcontractorInformation = vm.subcontractorInformation;
		this.pipeSizes = vm.pipeSizes;
		this.pipeTypes = vm.pipeTypes;
		this.manholeDepths = vm.manholeDepths;
		this.manholeFrameDiameters = vm.manholeFrameDiameters;
		this.informationsProvidedByCustomersBy = vm.informationsProvidedByCustomersBy;
		this.jc80gpm = vm.jc80gpm;
		this.sjc80gpm = vm.sjc80gpm;
		this.jcRecycler80 = vm.jcRecycler80;
		this.jjc120gpm = vm.jjc120gpm;
		this.jjc160gpm = vm.jjc160gpm;
		this.jcHiRail = vm.jcHiRail;
		this.jetter = vm.jetter;
		this.harbenJetter = vm.harbenJetter;
		this.hjJetter = vm.hjJetter;
		this.tvTruck = vm.tvTruck;
		this.tvLateralTk = vm.tvLateralTk;
		this.tvGroutTk = vm.tvGroutTk;
		this.tvLatGroutTk = vm.tvLatGroutTk;
		this.tvSonarTk = vm.tvSonarTk;
		this.tvPolaris = vm.tvPolaris;
		this.tvLCRTk = vm.tvLCRTk;
		this.tvLateralReinstator = vm.tvLateralReinstator;
		this.tvMTHTk = vm.tvMTHTk;
		this.tvLaserTk = vm.tvLaserTk;
		this.minicam = vm.minicam;
		this.vacTk = vm.vacTk;
		this.hivacTk = vm.hivacTk;
		this.hiRailvacTk = vm.hiRailvacTk;
		this.tanker = vm.tanker;
		this.wb = vm.wb;
		this.hwBoiler = vm.hwBoiler;
		this.pressureWash = vm.pressureWash;
		this.airComp = vm.airComp;
		this.clamTk = vm.clamTk;
		this.bucketMach = vm.bucketMach;
		this.mhRehabTk = vm.mhRehabTk;
		this.microtrax = vm.microtrax;
		this.smallEasementMachine = vm.smallEasementMachine;
		this.bigEasementMachine = vm.bigEasementMachine;
		this.arrowBoard = vm.arrowBoard;
		this.supportTk = vm.supportTk;
		this.rollOffTk = vm.rollOffTk;
		this.pump = vm.pump;
		this.pigLaunch = vm.pigLaunch;
		this.stoveMach = vm.stoveMach;
		this.powerRodder = vm.powerRodder;
		this.reeferTruck = vm.reeferTruck;
		this.lateralReinstatorTrailer = vm.lateralReinstatorTrailer;
		this.steamCureTruck = vm.steamCureTruck;
		this.airTesting = vm.airTesting;
		this.trackMachine = vm.trackMachine;
		this.additionalTruckEquipmentFields = vm.additionalTruckEquipmentFields;
		this.numberOfEmployees = vm.numberOfEmployees;
		this.customerContact = vm.customerContact;
		this.siteContact = vm.siteContact;
		this.billingContact = vm.billingContact;
		this.billTo = vm.billTo;
		this.billToAddress = vm.billToAddress;
		this.billToCityStateZip = vm.billToCityStateZip;
		this.billToPhoneNumber = vm.billToPhoneNumber;
		this.billToFaxNumber = vm.billToFaxNumber;
		this.billToContactName = vm.billToContactName;
		this.billToEmail = vm.billToEmail;
		this.additionalContacts = vm.additionalContacts;
		this.isDeliverable = vm.isDeliverable;
		this.deliverableAssigneeId = vm.deliverableAssigneeId;
		this.deliverableAssignee = vm.deliverableAssignee;
		this.deliverableSoftwareId = vm.deliverableSoftwareId;
		this.deliverableSoftware = vm.deliverableSoftware;
		this.deliverableFileFormatId = vm.deliverableFileFormatId;
		this.deliverableFileFormat = vm.deliverableFileFormat;
		this.deliverableCodeStandardId = vm.deliverableCodeStandardId;
		this.deliverableCodeStandard = vm.deliverableCodeStandard;
		this.deliverableDeliveryMethodId = vm.deliverableDeliveryMethodId;
		this.deliverableDeliveryMethod = vm.deliverableDeliveryMethod;
		this.deliverableDeliveryTimelineId = vm.deliverableDeliveryTimelineId;
		this.deliverableDeliveryTimeline = vm.deliverableDeliveryTimeline;
		this.deliverableNotes = vm.deliverableNotes;
		this.allowCustomerSignature = vm.allowCustomerSignature;
		this.startDate = vm.startDate;
		this.guaranteedCompletionDate = vm.guaranteedCompletionDate;
		this.targetCompletionDate = vm.targetCompletionDate;
		this.targetedDaysFromStart = vm.targetedDaysFromStart;
		this.guaranteedDaysFromStart = vm.guaranteedDaysFromStart;
		this.ableToCleanAndWashEquipmentAtCustomerLocation = vm.ableToCleanAndWashEquipmentAtCustomerLocation;
		this.cleanAndWashLocation = vm.cleanAndWashLocation;
		this.isWasteGeneratedSolid = vm.isWasteGeneratedSolid;
		this.isWasteGeneratedSludge = vm.isWasteGeneratedSludge;
		this.isWasteGeneratedLiquid = vm.isWasteGeneratedLiquid;
		this.isWasteHazardous = vm.isWasteHazardous;
		this.isWasteManifest = vm.isWasteManifest;
		this.wasteDescription = vm.wasteDescription;
		this.expectedWasteQuantity = vm.expectedWasteQuantity;
		this.disposalMethod = vm.disposalMethod;
		this.disposalInstructions = vm.disposalInstructions;
		this.disposalContact = vm.disposalContact;
		this.billingCodes = vm.billingCodes.map((b) => { return { ...b, databaseId: b.id, groupKey: `${b.groupId}`, editMode: null, groupColor: b.groupColor }; });
		this.billingCodeGroups = vm.billingCodeGroups.map((g) => {
			return {
				...g,
				databaseId: g.id,
				sortIndex: g.index,
				deletedAt: null,
			};
		});
		this.hazardSummary = vm.hazardSummary;
		this.permitsRequired = vm.permitsRequired;
		this.environmentalExposure = vm.environmentalExposure;
		this.environmentalExposureDescription = vm.environmentalExposureDescription;
		this.respiratoryProtection = vm.respiratoryProtection;
		this.respiratoryProtectionDescription = vm.respiratoryProtectionDescription;
		this.overheadHazards = vm.overheadHazards;
		this.overheadHazardsDescription = vm.overheadHazardsDescription;
		this.heightWork = vm.heightWork;
		this.heightWorkDescription = vm.heightWorkDescription;
		this.mechanicalHazards = vm.mechanicalHazards;
		this.mechanicalHazardsDescription = vm.mechanicalHazardsDescription;
		this.electricalHazards = vm.electricalHazards;
		this.electricalHazardsDescription = vm.electricalHazardsDescription;
		this.hazardousMaterials = vm.hazardousMaterials;
		this.hazardousMaterialsDescription = vm.hazardousMaterialsDescription;
		this.dangerousAnimalsOrPlants = vm.dangerousAnimalsOrPlants;
		this.dangerousAnimalsOrPlantsDescription = vm.dangerousAnimalsOrPlantsDescription;
		this.waterOrWetlands = vm.waterOrWetlands;
		this.waterOrWetlandsDescription = vm.waterOrWetlandsDescription;
		this.stormDrain = vm.stormDrain;
		this.stormDrainDescription = vm.stormDrainDescription;
		this.bypassPumping = vm.bypassPumping;
		this.bypassPumpingDescription = vm.bypassPumpingDescription;
		this.sensitiveEnvironment = vm.sensitiveEnvironment;
		this.sensitiveEnvironmentDescription = vm.sensitiveEnvironmentDescription;
		this.vehicleTraffic = vm.vehicleTraffic;
		this.vehicleTrafficDescription = vm.vehicleTrafficDescription;
		this.pedestrianCrossing = vm.pedestrianCrossing;
		this.pedestrianCrossingDescription = vm.pedestrianCrossingDescription;
		this.jobHazardAssessmentStatus = vm.jobHazardAssessmentStatus;
		this.directories = vm.directories;
		this.workRequestAttachments = vm.workRequestAttachments;
		this.isProject = vm.isProject;
		this.subjobs = vm.subjobs;
		this.projectId = vm.projectId;
		this.invoiceReminderRecipients = vm.invoiceReminderRecipients ?? [];
	}

	static fromJobCreateVM = (vm: W_Job_Create_VM): JobUpsertFM => {
		return {
			id: vm.id,
			jobCode: vm.jobCode,
			title: vm.title,
			salesId: vm.salesId,
			salesLead: vm.salesLead,
			salesLeadId: vm.salesLeadId,
			customerCompanyName: vm.customerCompanyName,
			workLocation: vm.workLocation ? WorkLocationAddressFM.fromVM(vm.workLocation) : null,
			projectManager: vm.projectManager,
			projectManagerId: vm.projectManagerId,
			officeId: vm.officeId,
			office: vm.office,
			divisionId: vm.divisionId,
			division: vm.division,
			isInternal: vm.isInternal,
			jobNotes: vm.jobNotes,
			estimateTotal: vm.estimateTotal,
			status: vm.status,
			priority: vm.priority,
			customerReferencePO: vm.customerReferencePO,
			isEmergencyJob: vm.isEmergencyJob,
			jobStatusId: vm.jobStatusId,
			jobStatus: vm.jobStatus,
			requestBondRequirementsNote: vm.requestBondRequirementsNote,
			requestHSERequirementsNote: vm.requestHSERequirementsNote,
			requestInsuranceRequirementsNote: vm.requestInsuranceRequirementsNote,
			requestJobNote: vm.requestJobNote,
			requestLiquidatedDamagesNote: vm.requestLiquidatedDamagesNote,
			requestMBEOrWBERequirementsNote: vm.requestMBEOrWBERequirementsNote,
			retainage: vm.retainage,
			targetDaysFromStart: vm.targetDaysFromStart,
			targetStartDate: vm.targetStartDate,
			travelLocationShort: vm.travelLocationShort,
			projectOwner: vm.projectOwner,
			ownerReference: vm.ownerReference,
			taxExempt: vm.taxExempt,
			generalContractor: vm.generalContractor,
			contractorReference: vm.contractorReference,
			isUnion: vm.isUnion,
			isPrevailingWage: vm.isPrevailingWage,
			jurisdiction: vm.jurisdiction,
			payrollDifferential: vm.payrollDifferential,
			surveyNeeded: vm.surveyNeeded,
			surveyedById: vm.surveyedById,
			surveyedBy: vm.surveyedBy,
			surveyDate: vm.surveyDate,
			waterAvailable: vm.waterAvailable,
			waterSupplyInformation: vm.waterSupplyInformation,
			subcontractorNeeded: vm.subcontractorNeeded,
			subcontractorInformation: vm.subcontractorInformation,
			pipeSizes: vm.pipeSizes,
			pipeTypes: vm.pipeTypes,
			manholeDepths: vm.manholeDepths,
			manholeFrameDiameters: vm.manholeFrameDiameters,
			informationsProvidedByCustomersBy: vm.informationsProvidedByCustomersBy,
			jc80gpm: vm.jc80gpm,
			sjc80gpm: vm.sjc80gpm,
			jcRecycler80: vm.jcRecycler80,
			jjc120gpm: vm.jjc120gpm,
			jjc160gpm: vm.jjc160gpm,
			jcHiRail: vm.jcHiRail,
			jetter: vm.jetter,
			harbenJetter: vm.harbenJetter,
			hjJetter: vm.hjJetter,
			tvTruck: vm.tvTruck,
			tvLateralTk: vm.tvLateralTk,
			tvGroutTk: vm.tvGroutTk,
			tvLatGroutTk: vm.tvLatGroutTk,
			tvSonarTk: vm.tvSonarTk,
			tvPolaris: vm.tvPolaris,
			tvLCRTk: vm.tvLCRTk,
			tvLateralReinstator: vm.tvLateralReinstator,
			tvMTHTk: vm.tvMTHTk,
			tvLaserTk: vm.tvLaserTk,
			minicam: vm.minicam,
			vacTk: vm.vacTk,
			hivacTk: vm.hivacTk,
			hiRailvacTk: vm.hiRailvacTk,
			tanker: vm.tanker,
			wb: vm.wb,
			hwBoiler: vm.hwBoiler,
			pressureWash: vm.pressureWash,
			airComp: vm.airComp,
			clamTk: vm.clamTk,
			bucketMach: vm.bucketMach,
			mhRehabTk: vm.mhRehabTk,
			microtrax: vm.microtrax,
			smallEasementMachine: vm.smallEasementMachine,
			bigEasementMachine: vm.bigEasementMachine,
			arrowBoard: vm.arrowBoard,
			supportTk: vm.supportTk,
			rollOffTk: vm.rollOffTk,
			pump: vm.pump,
			pigLaunch: vm.pigLaunch,
			stoveMach: vm.stoveMach,
			powerRodder: vm.powerRodder,
			reeferTruck: vm.reeferTruck,
			lateralReinstatorTrailer: vm.lateralReinstatorTrailer,
			steamCureTruck: vm.steamCureTruck,
			airTesting: vm.airTesting,
			trackMachine: vm.trackMachine,
			additionalTruckEquipmentFields: vm.additionalTruckEquipmentFields,
			numberOfEmployees: vm.numberOfEmployees,
			customerContact: vm.customerContact,
			siteContact: vm.siteContact,
			billingContact: vm.billingContact,
			billTo: vm.billTo,
			billToAddress: vm.billToAddress,
			billToCityStateZip: vm.billToCityStateZip,
			billToPhoneNumber: vm.billToPhoneNumber,
			billToFaxNumber: vm.billToFaxNumber,
			billToContactName: vm.billToContactName,
			billToEmail: vm.billToEmail,
			additionalContacts: vm.additionalContacts,
			isDeliverable: vm.isDeliverable,
			deliverableAssigneeId: vm.deliverableAssigneeId,
			deliverableAssignee: vm.deliverableAssignee,
			deliverableSoftwareId: vm.deliverableSoftwareId,
			deliverableSoftware: vm.deliverableSoftware,
			deliverableFileFormatId: vm.deliverableFileFormatId,
			deliverableFileFormat: vm.deliverableFileFormat,
			deliverableCodeStandardId: vm.deliverableCodeStandardId,
			deliverableCodeStandard: vm.deliverableCodeStandard,
			deliverableDeliveryMethodId: vm.deliverableDeliveryMethodId,
			deliverableDeliveryMethod: vm.deliverableDeliveryMethod,
			deliverableDeliveryTimelineId: vm.deliverableDeliveryTimelineId,
			deliverableDeliveryTimeline: vm.deliverableDeliveryTimeline,
			deliverableNotes: vm.deliverableNotes,
			allowCustomerSignature: vm.allowCustomerSignature,
			startDate: vm.startDate,
			guaranteedCompletionDate: vm.guaranteedCompletionDate,
			targetCompletionDate: vm.targetCompletionDate,
			targetedDaysFromStart: vm.targetedDaysFromStart,
			guaranteedDaysFromStart: vm.guaranteedDaysFromStart,
			ableToCleanAndWashEquipmentAtCustomerLocation: vm.ableToCleanAndWashEquipmentAtCustomerLocation,
			cleanAndWashLocation: vm.cleanAndWashLocation,
			isWasteGeneratedSolid: vm.isWasteGeneratedSolid,
			isWasteGeneratedSludge: vm.isWasteGeneratedSludge,
			isWasteGeneratedLiquid: vm.isWasteGeneratedLiquid,
			isWasteHazardous: vm.isWasteHazardous,
			isWasteManifest: vm.isWasteManifest,
			wasteDescription: vm.wasteDescription,
			expectedWasteQuantity: vm.expectedWasteQuantity,
			disposalMethod: vm.disposalMethod,
			disposalInstructions: vm.disposalInstructions,
			disposalContact: vm.disposalContact,
			billingCodes: vm.billingCodes.map((b) => { return { ...b, databaseId: b.id, groupKey: `${b.groupId}`, editMode: null, groupColor: b.groupColor }; }),
			billingCodeGroups: vm.billingCodeGroups.map((g) => {
				return {
					...g,
					databaseId: g.id,
					sortIndex: g.index,
					deletedAt: null,
				};
			}),
			hazardSummary: vm.hazardSummary,
			permitsRequired: vm.permitsRequired,
			environmentalExposure: vm.environmentalExposure,
			environmentalExposureDescription: vm.environmentalExposureDescription,
			respiratoryProtection: vm.respiratoryProtection,
			respiratoryProtectionDescription: vm.respiratoryProtectionDescription,
			overheadHazards: vm.overheadHazards,
			overheadHazardsDescription: vm.overheadHazardsDescription,
			heightWork: vm.heightWork,
			heightWorkDescription: vm.heightWorkDescription,
			mechanicalHazards: vm.mechanicalHazards,
			mechanicalHazardsDescription: vm.mechanicalHazardsDescription,
			electricalHazards: vm.electricalHazards,
			electricalHazardsDescription: vm.electricalHazardsDescription,
			hazardousMaterials: vm.hazardousMaterials,
			hazardousMaterialsDescription: vm.hazardousMaterialsDescription,
			dangerousAnimalsOrPlants: vm.dangerousAnimalsOrPlants,
			dangerousAnimalsOrPlantsDescription: vm.dangerousAnimalsOrPlantsDescription,
			waterOrWetlands: vm.waterOrWetlands,
			waterOrWetlandsDescription: vm.waterOrWetlandsDescription,
			stormDrain: vm.stormDrain,
			stormDrainDescription: vm.stormDrainDescription,
			bypassPumping: vm.bypassPumping,
			bypassPumpingDescription: vm.bypassPumpingDescription,
			sensitiveEnvironment: vm.sensitiveEnvironment,
			sensitiveEnvironmentDescription: vm.sensitiveEnvironmentDescription,
			vehicleTraffic: vm.vehicleTraffic,
			vehicleTrafficDescription: vm.vehicleTrafficDescription,
			pedestrianCrossing: vm.pedestrianCrossing,
			pedestrianCrossingDescription: vm.pedestrianCrossingDescription,
			jobHazardAssessmentStatus: vm.jobHazardAssessmentStatus,
			directories: vm.directories,
			workRequestAttachments: vm.workRequestAttachments,
			isProject: vm.isProject,
			subjobs: vm.subjobs,
			projectId: vm.projectId,
			invoiceReminderRecipients: vm.invoiceReminderRecipients ?? [],
		};
	};

	static fromFindJobVM = (vm: W_Job_FindJobForm_VM): JobUpsertFM => {
		return {
			id: vm.id,
			jobCode: vm.jobCode,
			title: vm.title,
			salesId: vm.salesId,
			salesLead: vm.salesLead,
			salesLeadId: vm.salesLeadId,
			customerCompanyName: vm.customerCompanyName,
			workLocation: vm.workLocation ? WorkLocationAddressFM.fromVM(vm.workLocation) : null,
			projectManager: vm.projectManager,
			projectManagerId: vm.projectManagerId,
			officeId: vm.officeId,
			office: vm.office,
			divisionId: vm.divisionId,
			division: vm.division,
			isInternal: vm.isInternal,
			jobNotes: vm.jobNotes,
			estimateTotal: vm.estimateTotal,
			status: vm.status,
			priority: vm.priority,
			customerReferencePO: vm.customerReferencePO,
			isEmergencyJob: vm.isEmergencyJob,
			jobStatusId: vm.jobStatusId,
			jobStatus: vm.jobStatus,
			requestBondRequirementsNote: vm.requestBondRequirementsNote,
			requestHSERequirementsNote: vm.requestHSERequirementsNote,
			requestInsuranceRequirementsNote: vm.requestInsuranceRequirementsNote,
			requestJobNote: vm.requestJobNote,
			requestLiquidatedDamagesNote: vm.requestLiquidatedDamagesNote,
			requestMBEOrWBERequirementsNote: vm.requestMBEOrWBERequirementsNote,
			retainage: vm.retainage,
			targetDaysFromStart: vm.targetDaysFromStart,
			targetStartDate: vm.targetStartDate,
			travelLocationShort: vm.travelLocationShort,
			projectOwner: vm.projectOwner,
			ownerReference: vm.ownerReference,
			taxExempt: vm.taxExempt,
			generalContractor: vm.generalContractor,
			contractorReference: vm.contractorReference,
			isUnion: vm.isUnion,
			isPrevailingWage: vm.isPrevailingWage,
			jurisdiction: vm.jurisdiction,
			payrollDifferential: vm.payrollDifferential,
			surveyNeeded: vm.surveyNeeded,
			surveyedById: vm.surveyedById,
			surveyedBy: vm.surveyedBy,
			surveyDate: vm.surveyDate,
			waterAvailable: vm.waterAvailable,
			waterSupplyInformation: vm.waterSupplyInformation,
			subcontractorNeeded: vm.subcontractorNeeded,
			subcontractorInformation: vm.subcontractorInformation,
			pipeSizes: vm.pipeSizes,
			pipeTypes: vm.pipeTypes,
			manholeDepths: vm.manholeDepths,
			manholeFrameDiameters: vm.manholeFrameDiameters,
			informationsProvidedByCustomersBy: vm.informationsProvidedByCustomersBy,
			jc80gpm: vm.jc80gpm,
			sjc80gpm: vm.sjc80gpm,
			jcRecycler80: vm.jcRecycler80,
			jjc120gpm: vm.jjc120gpm,
			jjc160gpm: vm.jjc160gpm,
			jcHiRail: vm.jcHiRail,
			jetter: vm.jetter,
			harbenJetter: vm.harbenJetter,
			hjJetter: vm.hjJetter,
			tvTruck: vm.tvTruck,
			tvLateralTk: vm.tvLateralTk,
			tvGroutTk: vm.tvGroutTk,
			tvLatGroutTk: vm.tvLatGroutTk,
			tvSonarTk: vm.tvSonarTk,
			tvPolaris: vm.tvPolaris,
			tvLCRTk: vm.tvLCRTk,
			tvLateralReinstator: vm.tvLateralReinstator,
			tvMTHTk: vm.tvMTHTk,
			tvLaserTk: vm.tvLaserTk,
			minicam: vm.minicam,
			vacTk: vm.vacTk,
			hivacTk: vm.hivacTk,
			hiRailvacTk: vm.hiRailvacTk,
			tanker: vm.tanker,
			wb: vm.wb,
			hwBoiler: vm.hwBoiler,
			pressureWash: vm.pressureWash,
			airComp: vm.airComp,
			clamTk: vm.clamTk,
			bucketMach: vm.bucketMach,
			mhRehabTk: vm.mhRehabTk,
			microtrax: vm.microtrax,
			smallEasementMachine: vm.smallEasementMachine,
			bigEasementMachine: vm.bigEasementMachine,
			arrowBoard: vm.arrowBoard,
			supportTk: vm.supportTk,
			rollOffTk: vm.rollOffTk,
			pump: vm.pump,
			pigLaunch: vm.pigLaunch,
			stoveMach: vm.stoveMach,
			powerRodder: vm.powerRodder,
			reeferTruck: vm.reeferTruck,
			lateralReinstatorTrailer: vm.lateralReinstatorTrailer,
			steamCureTruck: vm.steamCureTruck,
			airTesting: vm.airTesting,
			trackMachine: vm.trackMachine,
			additionalTruckEquipmentFields: vm.additionalTruckEquipmentFields,
			numberOfEmployees: vm.numberOfEmployees,
			customerContact: vm.customerContact,
			siteContact: vm.siteContact,
			billingContact: vm.billingContact,
			billTo: vm.billTo,
			billToAddress: vm.billToAddress,
			billToCityStateZip: vm.billToCityStateZip,
			billToPhoneNumber: vm.billToPhoneNumber,
			billToFaxNumber: vm.billToFaxNumber,
			billToContactName: vm.billToContactName,
			billToEmail: vm.billToEmail,
			additionalContacts: vm.additionalContacts,
			isDeliverable: vm.isDeliverable,
			deliverableAssigneeId: vm.deliverableAssigneeId,
			deliverableAssignee: vm.deliverableAssignee,
			deliverableSoftwareId: vm.deliverableSoftwareId,
			deliverableSoftware: vm.deliverableSoftware,
			deliverableFileFormatId: vm.deliverableFileFormatId,
			deliverableFileFormat: vm.deliverableFileFormat,
			deliverableCodeStandardId: vm.deliverableCodeStandardId,
			deliverableCodeStandard: vm.deliverableCodeStandard,
			deliverableDeliveryMethodId: vm.deliverableDeliveryMethodId,
			deliverableDeliveryMethod: vm.deliverableDeliveryMethod,
			deliverableDeliveryTimelineId: vm.deliverableDeliveryTimelineId,
			deliverableDeliveryTimeline: vm.deliverableDeliveryTimeline,
			deliverableNotes: vm.deliverableNotes,
			allowCustomerSignature: vm.allowCustomerSignature,
			startDate: vm.startDate,
			guaranteedCompletionDate: vm.guaranteedCompletionDate,
			targetCompletionDate: vm.targetCompletionDate,
			targetedDaysFromStart: vm.targetedDaysFromStart,
			guaranteedDaysFromStart: vm.guaranteedDaysFromStart,
			ableToCleanAndWashEquipmentAtCustomerLocation: vm.ableToCleanAndWashEquipmentAtCustomerLocation,
			cleanAndWashLocation: vm.cleanAndWashLocation,
			isWasteGeneratedSolid: vm.isWasteGeneratedSolid,
			isWasteGeneratedSludge: vm.isWasteGeneratedSludge,
			isWasteGeneratedLiquid: vm.isWasteGeneratedLiquid,
			isWasteHazardous: vm.isWasteHazardous,
			isWasteManifest: vm.isWasteManifest,
			wasteDescription: vm.wasteDescription,
			expectedWasteQuantity: vm.expectedWasteQuantity,
			disposalMethod: vm.disposalMethod,
			disposalInstructions: vm.disposalInstructions,
			disposalContact: vm.disposalContact,
			billingCodes: vm.billingCodes.map((b) => { return { ...b, databaseId: b.id, groupKey: `${b.groupId}`, editMode: null, groupColor: b.groupColor }; }),
			billingCodeGroups: vm.billingCodeGroups.map((g) => {
				return {
					...g,
					databaseId: g.id,
					sortIndex: g.index,
				};
			}),
			hazardSummary: vm.hazardSummary,
			permitsRequired: vm.permitsRequired,
			environmentalExposure: vm.environmentalExposure,
			environmentalExposureDescription: vm.environmentalExposureDescription,
			respiratoryProtection: vm.respiratoryProtection,
			respiratoryProtectionDescription: vm.respiratoryProtectionDescription,
			overheadHazards: vm.overheadHazards,
			overheadHazardsDescription: vm.overheadHazardsDescription,
			heightWork: vm.heightWork,
			heightWorkDescription: vm.heightWorkDescription,
			mechanicalHazards: vm.mechanicalHazards,
			mechanicalHazardsDescription: vm.mechanicalHazardsDescription,
			electricalHazards: vm.electricalHazards,
			electricalHazardsDescription: vm.electricalHazardsDescription,
			hazardousMaterials: vm.hazardousMaterials,
			hazardousMaterialsDescription: vm.hazardousMaterialsDescription,
			dangerousAnimalsOrPlants: vm.dangerousAnimalsOrPlants,
			dangerousAnimalsOrPlantsDescription: vm.dangerousAnimalsOrPlantsDescription,
			waterOrWetlands: vm.waterOrWetlands,
			waterOrWetlandsDescription: vm.waterOrWetlandsDescription,
			stormDrain: vm.stormDrain,
			stormDrainDescription: vm.stormDrainDescription,
			bypassPumping: vm.bypassPumping,
			bypassPumpingDescription: vm.bypassPumpingDescription,
			sensitiveEnvironment: vm.sensitiveEnvironment,
			sensitiveEnvironmentDescription: vm.sensitiveEnvironmentDescription,
			vehicleTraffic: vm.vehicleTraffic,
			vehicleTrafficDescription: vm.vehicleTrafficDescription,
			pedestrianCrossing: vm.pedestrianCrossing,
			pedestrianCrossingDescription: vm.pedestrianCrossingDescription,
			jobHazardAssessmentStatus: vm.jobHazardAssessmentStatus,
			directories: vm.directories,
			workRequestAttachments: vm.workRequestAttachments,
			isProject: vm.isProject,
			subjobs: vm.subjobs,
			projectId: vm.projectId,
			invoiceReminderRecipients: vm.invoiceReminderRecipients ?? [],
		};
	};

	static getAttributeName = (field: keyof JobUpsertFM) => field;

	static validateBillingCode = (
		billingCode: Required<JobUpsertFM>['billingCodes'][0],
		hasDuplicateLineItemNumber = false,
		hasDuplicateCustomerId = false
	) => {
		const errors: ValidationErrors = {};

		if (!billingCode.lineItemNumber) {
			errors.lineItemNumber = 'Line item number is required';
		} else if (hasDuplicateLineItemNumber) {
			errors.lineItemNumber = 'Duplicate line item number';
		}

		if (!isValidTextInput(billingCode.customerId)) {
			errors.customerId = 'Customer ID is required';
		} else if (hasDuplicateCustomerId) {
			errors.customerId = 'Duplicate customer ID';
		}

		if (!billingCode.unit) {
			errors.unit = 'Unit is required';
		}

		if (!billingCode.unitPrice) {
			errors.unitPrice = 'Unit price is required';
		} else if (+billingCode.unitPrice.split('.')[0] > MAX_BILLING_CODE_UNIT_PRICE_VALUE) {
			errors.unitPrice = `Unit Price value must be less than ${MAX_BILLING_CODE_UNIT_PRICE_VALUE}.`;
		} else if (+billingCode.unitPrice === 0) {
			errors.unitPrice = 'Unit price must be different from zero (0)';
		}

		if (!isValidTextInput(billingCode.description)) {
			errors.description = 'Description is required';
		}

		return errors;
	};

	static validate = (form: JobUpsertFM) => {
		const errors: ValidationErrors = {};

		if (!isValidTextInput(form?.jobCode)) {
			errors.jobCode = 'Job ID is required';
		}

		if (!isValidTextInput(form?.title)) {
			errors.title = 'Title is required';
		}

		if (!isValidTextInput(form?.customerCompanyName)) {
			errors.customerCompanyName = 'Customer company name is required';
		}

		if (!form?.officeId) {
			errors.officeId = 'Office is required';
		}

		if (form?.isDeliverable) {
			if (!form?.deliverableAssigneeId) {
				errors.deliverableAssigneeId = 'Assignee is required';
			}
		}

		if (!form.priority) {
			errors.priority = 'Priority is required';
		}

		if (form.workLocation && (!form.workLocation?.notes && (!form.workLocation?.street && !form.workLocation?.city && !form.workLocation?.neighborhood))) {
			errors.street = 'Work Location street or notes are required';
		}

		if (form.billingCodeGroups) {
			const nameCounter: Record<string, number> = {};

			for (const group of form.billingCodeGroups) {
				if (group.deletedAt) {
					continue;
				}
				const key = group.name?.trim();
				if (key) {
					nameCounter[key] = (nameCounter[key] ?? 0) + 1;
				}
			}

			errors.billingCodeGroups = [];
			errors._error = errors._error ?? undefined;

			for (let i = 0; i < form.billingCodeGroups.length; i++) {

				const group = form.billingCodeGroups[i];
				if (group.deletedAt) {
					continue;
				}
				const groupErrors: ValidationErrors = {};

				const nameKey = group.name?.trim();

				if (!isValidTextInput(group.name)) {
					groupErrors.name = 'Name is required';
				} else if (nameCounter[nameKey] > 1) {
					groupErrors.name = 'Duplicate group name';
				}

				errors.billingCodeGroups[i] = groupErrors;

				if (Object.keys(groupErrors).length) {
					errors._error = errors._error ?? 'billingCodeGroups';
				}
			}
		}

		if (form.billingCodes) {
			const lineItemNumberCounter: Record<number, number> = {};
			const customerIdCounter: Record<string, number> = {};

			for (const billingCode of form.billingCodes) {

				if (billingCode.deletedAt) {
					continue;
				}
				if (billingCode.lineItemNumber) {
					lineItemNumberCounter[billingCode.lineItemNumber] = (lineItemNumberCounter[billingCode.lineItemNumber] ?? 0) + 1;
				}
				if (billingCode.customerId) {
					customerIdCounter[billingCode.customerId] = (customerIdCounter[billingCode.customerId] ?? 0) + 1;
				}
			}

			errors.billingCodes = [];

			errors._error = undefined;

			for (let i = 0; i < form.billingCodes.length; i++) {
				const billingCode = form.billingCodes[i];

				if (billingCode.deletedAt) {
					continue;
				}

				const hasDuplicateLineItemNumber: boolean = !!billingCode.lineItemNumber && lineItemNumberCounter[billingCode.lineItemNumber] > 1;
				const hasDuplicateCustomerId: boolean = customerIdCounter[billingCode.customerId] > 1;

				errors.billingCodes[i] = JobUpsertFM.validateBillingCode(billingCode, hasDuplicateLineItemNumber, hasDuplicateCustomerId);

				// we add billing codes to global error variable, since we might not render billing codes
				// and if form does not render fields for all of the errors,
				// then the form might be considered valid as per ReduxForm documentation
				if (Object.keys(errors.billingCodes[i]).length !== 0) {
					errors._error = 'billingCodes';
				}
			}
		}

		if (!!form.uploadingAttachments && Object.keys(form.uploadingAttachments).length) {
			errors.uploadingAttachments = 'Attachment upload in progress.';
		}

		if (form.billToEmail && !EMAIL_REGEX.test(form.billToEmail)) {
			errors.billToEmail = 'Invalid email address';
		}

		if (form.payrollDifferential) {
			errors.payrollDifferential = {};

			if (form.payrollDifferential.value === null || form.payrollDifferential.value === undefined || Number.isNaN(form.payrollDifferential.value)) {
				errors.payrollDifferential.value = 'Value is required';
			}

			if (!form.payrollDifferential.workTypes?.length) {
				errors.payrollDifferential.workTypes = 'Work Type is required';
			}

			if (!Object.keys(errors.payrollDifferential).length) {
				delete errors.payrollDifferential;
			}
		}

		return errors;
	};

	static warn = (form: JobUpsertFM) => {
		const warnings: ValidationErrors = {};

		if (
			form?.workLocation &&
			(isNullOrUndefined(form.workLocation.backup?.latitude) || isNullOrUndefined(form.workLocation.backup?.longitude))
		) {
			warnings.workLocation = { location: 'Work location not recognized by Google Maps.' };
		}

		return warnings;
	};

	static normalizeNumberOfEmployees = (_value) => {
		if (+_value < 0) {
			return 0;
		}

		// Float doesnt make sense so just return floor
		if (!Number.isInteger(+_value)) {
			return Math.floor(_value);
		}

		return _value;
	};

	static toRM(form: Partial<JobUpsertFM>): Partial<JobUpsertRM.Default> {
		const rm: Partial<JobUpsertRM.Default> = {};

		for (const _key of Object.keys(form) as (keyof JobUpsertFM)[]) {
			switch (_key) {
				// Skip VM properties that we only need ids from or dont need at all in RM
				case 'office':
				case 'salesLead':
				case 'projectManager':
				case 'division':
				case 'deliverableSoftware':
				case 'deliverableAssignee':
				case 'deliverableDeliveryMethod':
				case 'deliverableCodeStandard':
				case 'deliverableDeliveryTimeline':
				case 'deliverableFileFormat':
				case 'jobStatus':
				case 'guaranteedDaysFromStart':
				case 'targetDaysFromStart':
				case 'uploadingAttachments':
					break;

				case 'surveyDate':
				case 'startDate':
				case 'targetStartDate':
				case 'targetCompletionDate':
				case 'guaranteedCompletionDate':
					rm[_key] = form[_key] ? TimeUtils.formatDate(form[_key], TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY) : null;
					break;

				case 'workLocation': {
					// if location and notes are deleted
					if (!form[_key]?.street && !form[_key]?.notes && !form[_key]?.city && !form[_key]?.location) {
						rm[_key] = null;
						break;
					}

					rm[_key] = form[_key];
					break;
				}

				case 'billToPhoneNumber': {
					if (!!form[_key]) {
						rm[_key] = toRawPhoneNumber(form[_key]!);
					}
					break;
				}

				case 'additionalTruckEquipmentFields': {
					for (let i = 0; i < MAX_WR_TRUCK_EQUIPMENT_ADDITIONAL_FIELDS; i++) {
						rm[`additionalTruckEquipmentField${i + 1}Name`] = form[_key]?.[i]?.name ?? null;
						rm[`additionalTruckEquipmentField${i + 1}Checked`] = form[_key]?.[i]?.isChecked ?? false;
					}
					break;
				}

				case 'payrollDifferential': {
					rm.payrollDifferential = form.payrollDifferential
						? {
							value: form.payrollDifferential.value,
							workTypes: form.payrollDifferential.workTypes ?? [],
						}
						: null;

					break;
				}

				case 'workRequestAttachments': {
					rm.workRequestAttachments = form.workRequestAttachments?.map((_wra) => ({
						attachmentId: _wra.id,
						copyToWorkOrder: _wra.copyToWorkOrderFlag,
					}));
					break;
				}

				case 'subjobs': {
					rm.subjobIds = form.subjobs?.map(getId);
					break;
				}

				case 'billingCodes': {
					rm.billingCodes = form.billingCodes?.map((_billingCode) => ({
						lineItemNumber: _billingCode.lineItemNumber ? +_billingCode.lineItemNumber : 0,
						customerNumber: _billingCode.customerNumber ? +_billingCode.customerNumber : null,
						customerId: _billingCode.customerId,
						ownerNumber: _billingCode.ownerNumber ? _billingCode.ownerNumber : null,
						ownerId: _billingCode.ownerId ? _billingCode.ownerId : null,
						unit: _billingCode.unit!,
						unitPrice: _billingCode.unitPrice,
						bidQuantity: _billingCode.bidQuantity ? _billingCode.bidQuantity : null,
						groupId: _billingCode.groupId ?? null,
						groupName: _billingCode.group ?? null,
						description: _billingCode.description,
						id: typeof (_billingCode.id) === 'string' ? undefined : _billingCode.id,
						deletedAt: _billingCode.deletedAt ?? null,
						editMode: _billingCode.editMode ?? null,
					})) ?? [];
					break;
				}

				case 'billingCodeGroups': {
					rm.billingCodeGroups = form.billingCodeGroups?.map((g) => ({
						id: g.databaseId ?? null,
						name: g.name,
						color: g.color,
						index: g.sortIndex,
						deletedAt: g.deletedAt ?? null,
					})) ?? [];
					break;
				}

				case 'requestJobNote':
					rm.summaryOfWork = form.requestJobNote;
					break;

				case 'invoiceReminderRecipients':
					if (form.invoiceReminderRecipients) {
						rm.invoiceReminderRecipients = { accountIds: form.invoiceReminderRecipients.map((r) => r.accountId) };
						break;
					}

				case 'additionalContacts':
					if (form.additionalContacts?.length) {
						rm.additionalContacts = form.additionalContacts.filter((contact) => contact !== null) ?? [];
						break;
					}

				default:
					rm[_key] = form[_key];
					break;
			}
		}
		return rm;
	}

	static toCreateRequestModel(form: JobUpsertFM): W_Job_Create_RM {
		let workLocation = form.workLocation ?? null;
		if (!form.workLocation?.street && !form.workLocation?.notes && !form.workLocation?.city && !form.workLocation?.location) {
			workLocation = null;
		}

		return {
			// Sales
			salesId: form.salesId,
			salesLeadId: form.salesLeadId,
			estimateTotal: form.estimateTotal,
			// Job Summary
			jobCode: form.jobCode,
			title: form.title,
			isInternal: form.isInternal,
			customerCompanyName: form.customerCompanyName,
			customerReferencePO: form.customerReferencePO,
			isEmergencyJob: form.isEmergencyJob,
			priority: form.priority,
			jobStatusId: form.jobStatusId,
			projectManagerId: form.projectManagerId,
			officeId: form.officeId,
			divisionId: form.divisionId,
			// Job Detail
			projectOwner: form.projectOwner,
			ownerReference: form.ownerReference,
			taxExempt: form.taxExempt,
			generalContractor: form.generalContractor,
			contractorReference: form.contractorReference,
			isUnion: form.isUnion,
			isPrevailingWage: form.isPrevailingWage,
			jurisdiction: form.jurisdiction,
			payrollDifferential: form.payrollDifferential,
			jobNotes: form.jobNotes,
			// Requirements
			requestMBEOrWBERequirementsNote: form.requestMBEOrWBERequirementsNote,
			requestBondRequirementsNote: form.requestBondRequirementsNote,
			requestHSERequirementsNote: form.requestHSERequirementsNote,
			retainage: form.retainage,
			requestInsuranceRequirementsNote: form.requestInsuranceRequirementsNote,
			requestLiquidatedDamagesNote: form.requestLiquidatedDamagesNote,
			// Schedule Info
			targetStartDate: form.targetStartDate
				? TimeUtils.formatDate(form.targetStartDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			targetCompletionDate: form.targetCompletionDate
				? TimeUtils.formatDate(form.targetCompletionDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			startDate: form.startDate
				? TimeUtils.formatDate(form.startDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			guaranteedCompletionDate: form.guaranteedCompletionDate
				? TimeUtils.formatDate(form.guaranteedCompletionDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			// Contacts
			customerContact: form.customerContact,
			siteContact: form.siteContact,
			billingContact: form.billingContact,
			billTo: form.billTo,
			billToAddress: form.billToAddress,
			billToCityStateZip: form.billToCityStateZip,
			billToPhoneNumber: form.billToPhoneNumber ? toRawPhoneNumber(form.billToPhoneNumber) : null,
			billToFaxNumber: form.billToFaxNumber,
			billToContactName: form.billToContactName,
			billToEmail: form.billToEmail,
			additionalContacts: form.additionalContacts,
			invoiceReminderRecipients: { accountIds: (form.invoiceReminderRecipients ?? []).map((r) => r.accountId) },
			// Deliverables
			isDeliverable: form.isDeliverable,
			deliverableAssigneeId: form.deliverableAssigneeId,
			deliverableSoftwareId: form.deliverableSoftwareId,
			deliverableFileFormatId: form.deliverableFileFormatId,
			deliverableCodeStandardId: form.deliverableCodeStandardId,
			deliverableDeliveryMethodId: form.deliverableDeliveryMethodId,
			deliverableDeliveryTimelineId: form.deliverableDeliveryTimelineId,
			deliverableNotes: form.deliverableNotes,
			// Work Location
			workLocation,
			// Summary of Work
			summaryOfWork: form.requestJobNote,
			// Survey data
			surveyNeeded: form.surveyNeeded,
			surveyedById: form.surveyedById,
			surveyDate: form.surveyDate
				? TimeUtils.formatDate(form.surveyDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			waterAvailable: form.waterAvailable,
			waterSupplyInformation: form.waterSupplyInformation,
			subcontractorNeeded: form.subcontractorNeeded,
			subcontractorInformation: form.subcontractorInformation,
			pipeSizes: form.pipeSizes,
			pipeTypes: form.pipeTypes,
			manholeDepths: form.manholeDepths,
			manholeFrameDiameters: form.manholeFrameDiameters,
			informationsProvidedByCustomersBy: form.informationsProvidedByCustomersBy,
			jc80gpm: form.jc80gpm,
			sjc80gpm: form.sjc80gpm,
			jcRecycler80: form.jcRecycler80,
			jjc120gpm: form.jjc120gpm,
			jjc160gpm: form.jjc160gpm,
			jcHiRail: form.jcHiRail,
			jetter: form.jetter,
			harbenJetter: form.harbenJetter,
			hjJetter: form.hjJetter,
			tvTruck: form.tvTruck,
			tvLateralTk: form.tvLateralTk,
			tvGroutTk: form.tvGroutTk,
			tvLatGroutTk: form.tvLatGroutTk,
			tvSonarTk: form.tvSonarTk,
			tvPolaris: form.tvPolaris,
			tvLCRTk: form.tvLCRTk,
			tvLateralReinstator: form.tvLateralReinstator,
			tvMTHTk: form.tvMTHTk,
			tvLaserTk: form.tvLaserTk,
			minicam: form.minicam,
			vacTk: form.vacTk,
			hivacTk: form.hivacTk,
			hiRailvacTk: form.hiRailvacTk,
			tanker: form.tanker,
			wb: form.wb,
			hwBoiler: form.hwBoiler,
			pressureWash: form.pressureWash,
			airComp: form.airComp,
			clamTk: form.clamTk,
			bucketMach: form.bucketMach,
			mhRehabTk: form.mhRehabTk,
			microtrax: form.microtrax,
			smallEasementMachine: form.smallEasementMachine,
			bigEasementMachine: form.bigEasementMachine,
			arrowBoard: form.arrowBoard,
			supportTk: form.supportTk,
			rollOffTk: form.rollOffTk,
			pump: form.pump,
			pigLaunch: form.pigLaunch,
			stoveMach: form.stoveMach,
			powerRodder: form.powerRodder,
			reeferTruck: form.reeferTruck,
			lateralReinstatorTrailer: form.lateralReinstatorTrailer,
			steamCureTruck: form.steamCureTruck,
			airTesting: form.airTesting,
			trackMachine: form.trackMachine,
			additionalTruckEquipmentField1Name: form.additionalTruckEquipmentFields?.[0]?.name ?? null,
			additionalTruckEquipmentField1Checked: form.additionalTruckEquipmentFields?.[0]?.isChecked ?? null,
			additionalTruckEquipmentField2Name: form.additionalTruckEquipmentFields?.[1]?.name ?? null,
			additionalTruckEquipmentField2Checked: form.additionalTruckEquipmentFields?.[1]?.isChecked ?? null,
			numberOfEmployees: form.numberOfEmployees,
			// Disposal
			ableToCleanAndWashEquipmentAtCustomerLocation: form.ableToCleanAndWashEquipmentAtCustomerLocation,
			cleanAndWashLocation: form.cleanAndWashLocation,
			isWasteGeneratedSolid: form.isWasteGeneratedSolid,
			isWasteGeneratedSludge: form.isWasteGeneratedSludge,
			isWasteGeneratedLiquid: form.isWasteGeneratedLiquid,
			isWasteHazardous: form.isWasteHazardous,
			isWasteManifest: form.isWasteManifest,
			wasteDescription: form.wasteDescription,
			expectedWasteQuantity: form.expectedWasteQuantity,
			disposalMethod: form.disposalMethod,
			disposalInstructions: form.disposalInstructions,
			disposalContact: form.disposalContact,
			// Job Hazard Summary
			hazardSummary: form.hazardSummary,
			permitsRequired: form.permitsRequired,
			// Safety
			environmentalExposure: form.environmentalExposure,
			environmentalExposureDescription: form.environmentalExposureDescription,
			respiratoryProtection: form.respiratoryProtection,
			respiratoryProtectionDescription: form.respiratoryProtectionDescription,
			overheadHazards: form.overheadHazards,
			overheadHazardsDescription: form.overheadHazardsDescription,
			heightWork: form.heightWork,
			heightWorkDescription: form.heightWorkDescription,
			mechanicalHazards: form.mechanicalHazards,
			mechanicalHazardsDescription: form.mechanicalHazardsDescription,
			electricalHazards: form.electricalHazards,
			electricalHazardsDescription: form.electricalHazardsDescription,
			hazardousMaterials: form.hazardousMaterials,
			hazardousMaterialsDescription: form.hazardousMaterialsDescription,
			dangerousAnimalsOrPlants: form.dangerousAnimalsOrPlants,
			dangerousAnimalsOrPlantsDescription: form.dangerousAnimalsOrPlantsDescription,
			// Environmental Protection
			waterOrWetlands: form.waterOrWetlands,
			waterOrWetlandsDescription: form.waterOrWetlandsDescription,
			stormDrain: form.stormDrain,
			stormDrainDescription: form.stormDrainDescription,
			bypassPumping: form.bypassPumping,
			bypassPumpingDescription: form.bypassPumpingDescription,
			sensitiveEnvironment: form.sensitiveEnvironment,
			sensitiveEnvironmentDescription: form.sensitiveEnvironmentDescription,
			// Public Safety
			vehicleTraffic: form.vehicleTraffic,
			vehicleTrafficDescription: form.vehicleTrafficDescription,
			pedestrianCrossing: form.pedestrianCrossing,
			pedestrianCrossingDescription: form.pedestrianCrossingDescription,
			billingCodes: form.billingCodes?.map((_billingCode) => {
				// We need these to be nullable in FM for data initialization
				if (!_billingCode.lineItemNumber) {
					throw new Error('Missing lineItemNumber');
				}
				if (!_billingCode.unit) {
					throw new Error('Missing unit');
				}
				return ({
					lineItemNumber: +_billingCode.lineItemNumber,
					customerNumber: _billingCode.customerNumber ? +_billingCode.customerNumber : null,
					customerId: _billingCode.customerId,
					ownerNumber: _billingCode.ownerNumber ? _billingCode.ownerNumber : null,
					ownerId: _billingCode.ownerId ? _billingCode.ownerId : null,
					unit: _billingCode.unit,
					unitPrice: _billingCode.unitPrice,
					bidQuantity: _billingCode.bidQuantity ? _billingCode.bidQuantity : null,
					groupId: _billingCode.groupId,
					groupName: _billingCode.group,
					description: _billingCode.description,
					id: typeof (_billingCode.id) === 'string' ? undefined : _billingCode.id,
				});

			}) ?? [],
			billingCodeGroups: form.billingCodeGroups?.map((_billingCodeGroup) => ({
				id: +_billingCodeGroup.id,
				name: _billingCodeGroup.name,
				color: _billingCodeGroup.color,
				index: _billingCodeGroup.sortIndex,
			})),
			allowCustomerSignature: form.allowCustomerSignature,
			isProject: form.isProject,
			subjobIds: (form.subjobs ?? []).map(getId),
			// Attachments
			directories: form.directories,
			workRequestAttachments: (form.workRequestAttachments ?? []).map((_wra) => ({
				attachmentId: _wra.id,
				copyToWorkOrder: _wra.copyToWorkOrderFlag,
			})),
			uploadedAttachmentIds: form.uploadedAttachmentIds ?? [],
		};
	}
}

export default JobUpsertFM;
